import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import restClient from 'restClient'
import appStorage from 'storage/appStorage'
import { PremiumProductDetailType, ReduxActionType } from 'types/global'
import { SET_ORG_ID } from './auth'
export const GET_PREMIUM_PRODUCTS = 'GET_PREMIUM_PRODUCTS'
export const GET_PREMIUM_PRODUCTS_SUCCESS = 'GET_PREMIUM_PRODUCTS_SUCCESS'
export const GET_PREMIUM_PRODUCTS_FAIL = 'GET_PREMIUM_PRODUCTS_FAIL'

export type PremiumProductsStoreType = {
  availableProducts: PremiumProductDetailType[] | null
  loadedOrgId: number | null
  loading: boolean
  fetchFailed: boolean
}

const initialState: PremiumProductsStoreType = {
  availableProducts: null,
  loadedOrgId: null,
  loading: false,
  fetchFailed: false,
}

export default (state: PremiumProductsStoreType = initialState, action: ReduxActionType) => {
  switch (action.type) {
    case SET_ORG_ID:
      // Wallet products are org-scoped; clear stale list before refetch (see premiumProductsSagas).
      return { ...initialState }
    case GET_PREMIUM_PRODUCTS: {
      const requestedOrgId = action?.payload?.orgId ?? null
      const isOrgChange = requestedOrgId != null && state.loadedOrgId !== requestedOrgId
      return {
        ...state,
        loading: true,
        fetchFailed: false,
        ...(isOrgChange ? { availableProducts: null, loadedOrgId: null } : {}),
      }
    }
    case GET_PREMIUM_PRODUCTS_SUCCESS:
      return {
        ...state,
        loading: false,
        fetchFailed: false,
        availableProducts: action?.payload?.data?.available_products,
        loadedOrgId: action?.payload?.orgId ?? null,
      }
    case GET_PREMIUM_PRODUCTS_FAIL:
      return {
        ...state,
        loading: false,
        fetchFailed: true,
        availableProducts: [],
        loadedOrgId: action?.payload?.orgId ?? state.loadedOrgId,
      }
  }
  return state
}

export function* getPremiumProductsSaga(action: ReduxActionType) {
  const orgId = action?.payload?.orgId ?? appStorage.getOrgId()
  try {
    const availableProducts = yield call(doGetPremiumProducts)
    yield put({
      type: GET_PREMIUM_PRODUCTS_SUCCESS,
      payload: { data: { available_products: availableProducts }, orgId },
    })
  } catch (e) {
    yield put({ type: GET_PREMIUM_PRODUCTS_FAIL, payload: { orgId } })
  }
}

export const doGetPremiumProducts = () => {
  return new Promise((resolve, reject) => {
    const restClientInstance = restClient(window.API_ROOT + '/api')
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${appStorage.getOrgId()}/wallet_products/?paginate=false&fieldset=list`,
    })
      .then((response: any) => {
        resolve(response.data)
      })
      .catch((err: any) => {
        console.log('err', err)
        reject(false)
      })
  })
}

export const getPremiumProducts = (orgId?: number | null) => {
  return {
    type: GET_PREMIUM_PRODUCTS,
    payload: { orgId: orgId ?? appStorage.getOrgId() },
  }
}

export type PremiumProductsRefreshStore = {
  dispatch: (action: ReduxActionType) => void
  getState: () => unknown
  subscribe: (listener: () => void) => () => void
}

/** Dispatches GET_PREMIUM_PRODUCTS and resolves when the saga finishes for the given org. */
export function waitForPremiumProductsRefresh(orgId: number, store: PremiumProductsRefreshStore): Promise<void> {
  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      unsubscribe()
      resolve()
    }

    const unsubscribe = store.subscribe(() => {
      const premiumProducts = (store.getState() as { premiumProducts?: PremiumProductsStoreType }).premiumProducts
      if (premiumProducts && !premiumProducts.loading && premiumProducts.loadedOrgId === orgId) {
        finish()
      }
    })

    store.dispatch(getPremiumProducts(orgId))
  })
}

export const premiumProductsSelectors = {
  getIsLoading: (state: any): boolean => !!state.premiumProducts?.loading,
  getProducts: (state: any): PremiumProductDetailType[] | null => state.premiumProducts?.availableProducts ?? null,
  getLoadedOrgId: (state: any): number | null => state.premiumProducts?.loadedOrgId ?? null,
  getFetchFailed: (state: any): boolean => !!state.premiumProducts?.fetchFailed,
}

function* refetchPremiumProductsOnOrgChange() {
  yield put(getPremiumProducts())
}

export function* premiumProductsSagas() {
  yield all([
    takeLatest(GET_PREMIUM_PRODUCTS, getPremiumProductsSaga),
    takeEvery(SET_ORG_ID, refetchPremiumProductsOnOrgChange),
  ])
}
